import { useGoals } from 'features/goals';
import { useLocalizedLifeAreas } from 'features/life-areas';
import { useTags } from 'features/tags/hooks/use-tags';
import React, { useMemo } from 'react';
import { SearchGoalCard } from 'shared/components/connected/search-goal-card';
import { SearchLifeAreaCard } from 'shared/components/connected/search-life-area-card';
import { SearchTagCard } from 'shared/components/connected/search-tag-card';
import { useEntityLinks } from 'shared/hooks/use-entity-links';
import { Goal } from 'shared/types/goal';
import { ID } from 'shared/types/id';
import { LifeArea } from 'shared/types/life-area';
import { Tag } from 'shared/types/tag';

export type DetailPageLinkProps = {
  id: ID;
  link: string;
  onClick?: () => void;
};

export const DetailPageLink: React.FC<DetailPageLinkProps> = ({
  id,
  link,
  onClick,
}) => {
  const lifeAreas = useLocalizedLifeAreas();
  const links = useEntityLinks();
  const goals = useGoals();
  const tags = useTags();

  const _onClick = () => onClick?.();

  const goalsMap = useMemo<Record<ID, Goal>>(
    () => goals.reduce((acc, goal) => ({ ...acc, [goal.id]: goal }), {}),
    [goals],
  );
  const lifeAreasMap = useMemo<Record<ID, LifeArea>>(
    () =>
      lifeAreas.reduce(
        (acc, lifeArea) => ({ ...acc, [lifeArea.id]: lifeArea }),
        {},
      ),
    [lifeAreas],
  );
  const tagsMap = useMemo<Record<ID, Tag>>(
    () => tags.reduce((acc, tag) => ({ ...acc, [tag.id]: tag }), {}),
    [tags],
  );

  const item = useMemo(
    () => goalsMap[id] ?? lifeAreasMap[id] ?? tagsMap[id],
    [goalsMap, id, lifeAreasMap, tagsMap],
  );

  if (!item) {
    return null;
  }

  if ('lifeAreaId' in item) {
    return (
      <SearchGoalCard
        goal={item}
        goals={goals}
        lifeAreas={lifeAreas}
        links={links}
        navTo={link}
        onClick={_onClick}
      />
    );
  }

  if ('iconName' in item) {
    return (
      <SearchLifeAreaCard lifeArea={item} navTo={link} onClick={_onClick} />
    );
  }

  return <SearchTagCard tag={item} navTo={link} onClick={() => onClick?.()} />;
};
